import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { shopifyPluginUrl } from '@/lib/config';
import {
  DatasourceInstructionMode,
  GetBrandApiKeyDocument,
  ShopifyPluginInstalledDocument,
} from '@/lib/graphql/generated/graphql';
import { useQuery, useSubscription } from '@apollo/client/react';
import { ArrowLeft, Check, Copy, ExternalLink, Loader2 } from 'lucide-react';

import { Shopify } from '../icons/shopify';
import { ScrollArea } from '../ui/scroll-area';
import { BaseImportViewProps } from './import-view-props';

interface ViewInstallShopifyProps extends BaseImportViewProps {
  brandId: string;
  instructionMode?: DatasourceInstructionMode;
  onModifyOption?: () => void;
  onCloseDialog?: () => void;
  refetchDeal: () => Promise<unknown>;
}

export const ViewInstallShopifyPlugin: React.FC<ViewInstallShopifyProps> = ({
  brandId,
  setImportView,
  refetchDeal,
  dealId,
}) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [hasCopied, setHasCopied] = useState(false);

  const [shop, setShop] = useState<string | null>(null);

  const { data: brandApiKeyData } = useQuery(GetBrandApiKeyDocument, {
    variables: {
      brandId: brandId,
    },
    skip: !brandId,
  });

  const { data: installedData } = useSubscription(
    ShopifyPluginInstalledDocument,
    {
      variables: {
        brandId: brandId,
      },
      skip: !brandId,
    }
  );

  const apiKey = brandApiKeyData?.getBrandApiKey;
  const isInstalled = !!installedData?.shopifyInstalled;

  useEffect(() => {
    if (installedData?.shopifyInstalled) {
      setShop(installedData.shopifyInstalled);
      void refetchDeal();
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [installedData?.shopifyInstalled, dealId]);

  const handleCopy = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      setHasCopied(true);
      toast({
        title: t('components.copied_to_clipboard'),
      });
      setTimeout(() => setHasCopied(false), 2000);
    }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={() => setImportView('products')}
            aria-label={t('components.import_flow.back_to_options')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>
            {' '}
            {t('components.import_flow.shopify.install_title')}
          </DialogTitle>
        </div>
      </DialogHeader>
      <ScrollArea className="h-[500px]">
        <div className="mt-6 space-y-8">
          {/* Step 1: Install Plugin */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                1
              </div>
              <h3 className="font-semibold">
                {t('components.import_flow.shopify.step_1_title')}
              </h3>
            </div>
            <div className="pl-8">
              <a
                href={`${shopifyPluginUrl}${i18n.language}`}
                target="_blank"
                rel="noreferrer"
                className="inline-block w-full"
              >
                <Button
                  variant="secondary"
                  className="h-16 w-full justify-start text-left hover:bg-muted/50"
                >
                  <Shopify fill="#96BF48" className="size-12" />

                  <p className="text-md flex-1 font-medium">
                    {t('components.import_flow.shopify.step_1_description')}
                  </p>

                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </Button>
              </a>
            </div>
          </div>

          {/* Step 2: Configure API Key */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                2
              </div>
              <h3 className="font-semibold">
                {t('components.import_flow.shopify.step_2_title')}
              </h3>
            </div>

            <div className="space-y-3 pl-8">
              <p className="text-sm text-muted-foreground">
                {t('components.import_flow.shopify.step_2_description')}
              </p>

              {apiKey ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleCopy}
                    className="h-16 w-full justify-start text-left hover:bg-muted/50"
                  >
                    <p className="text-md flex-1 font-medium">{apiKey}</p>

                    {hasCopied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex h-10 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                  {t('components.import_flow.shopify.loading_api_key')}
                </div>
              )}
            </div>
          </div>

          {/* Step 3: Installation Status */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                3
              </div>
              <h3 className="font-semibold">
                {t('components.import_flow.shopify.step_3_title')}
              </h3>
            </div>

            <div className="pl-8">
              {isInstalled ? (
                <span
                  className={`inline-flex w-full items-center rounded-md bg-green-100 px-2 py-4 text-xs font-medium text-green-800`}
                >
                  <Check className="mr-2 h-5 w-5" />
                  <p className="text-md">
                    {t('components.import_flow.shopify.step_3_success', {
                      shop,
                    })}
                  </p>
                </span>
              ) : (
                <Button
                  variant="outline"
                  disabled
                  className="h-16 w-full justify-start text-left hover:bg-muted/50"
                >
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <p>{t('components.import_flow.shopify.step_3_waiting')}</p>
                </Button>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>
      {isInstalled ? (
        <DialogFooter>
          <Button variant="outline" onClick={() => setImportView('products')}>
            {t('general.previous')}
          </Button>
          <Button
            onClick={() => setImportView('productsShopifyOptionSelection')}
          >
            {t('general.importation_confirm')}
          </Button>
        </DialogFooter>
      ) : null}
    </>
  );
};
